import React, { Component } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import { fromJS, Map, List } from 'immutable';
import PropTypes from 'prop-types';

import { removeViewstate, removeGlobalSearch, fetchCart } from '../actions/actionCreators';
import { CART_API_BASE_URL } from '../constants/endpoints';
import connect from '../store/connect';
import Header from '../components/Header/Header';
import Footer from '../components/Footer';
import CartWrapper from '../components/CartWrapper';
import StartPage from '../pages/StartPage';
import ArticlePage from '../pages/ArticlePage';
import CategoryLink from '../pages/CategoryLink';
import CategoryPage from '../pages/CategoryPage';
import ProductPage from '../pages/ProductPage';
import CheckoutPage from '../pages/CheckoutPage';
import SearchResultPage from '../pages/SearchResultPage';
import ErrorPage from '../pages/ErrorPage';
import BrandListPage from '../pages/BrandListPage';
import BrandPage from '../pages/BrandPage';
import CampaignListPage from '../pages/CampaignListPage';
import CampaignPage from '../pages/CampaignPage';
import ParentCategoryPage from '../pages/ParentCategoryPage';
import OrderConfirmationPage from '../pages/OrderConfirmationPage';
import StoreItemListPage from '../pages/StoreItemListPage';
import StoreItemPage from '../pages/StoreItemPage';
import ProductListPage from '../pages/ProductListPage';
import NewsletterPage from '../pages/NewsletterPage';
import { analyticsPageViewEvent } from '../util/analyticEvents';
import {
  CHECKOUT_PAGE_TYPE,
  CAMPAIGN_LIST_PAGE_TYPE,
  CAMPAIGN_PAGE_TYPE,
  PRODUCT_LIST_PAGE_TYPE,
  NEWSLETTER_PAGE_TYPE
} from '../constants/pageTypes';
import History from '../history';

class Routes extends Component {
  componentDidMount() {
    // PAGE VIEW EVENT TRIGGER
    analyticsPageViewEvent();
    this.props.fetchCart({ endpointUrl: `${CART_API_BASE_URL}/get`, method: 'GET' });

    if (document.getElementById('react_root').dataset.litiumPreview) {
      History.push(document.getElementById('react_root').dataset.litiumPreview);
    }
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.location.pathname !== this.props.location.pathname) {
      this.props.fetchCart({ endpointUrl: `${CART_API_BASE_URL}/get`, method: 'GET' });
      // PAGE VIEW EVENT TRIGGER
      analyticsPageViewEvent();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location.pathname !== this.props.location.pathname) {
      // reset viewstates for overlays on route change
      this.props.removeViewstate('mobileMenuActive');
      this.props.removeViewstate('activeMenuItem');
      this.props.removeViewstate('globalSearchActive');
      // this.props.removeGlobalSearch();

      if (!this.navigatingBetweenProductVariants(prevProps.location.pathname, this.props.location.pathname)) {
        // scroll page to top on route change (but not on URL replace)
        window.scrollTo(0, 0);
      }
    }
  }

  getBaseProduct(url) {
    // method to return the first part of a product url
    const splitUrl = url.split('/');
    const lastPartOfUrl = splitUrl.pop();
    const splitLastPartOfUrl = lastPartOfUrl.split('-');

    return { firstPart: splitUrl.join('/'), lastPart: splitLastPartOfUrl[0] };
  }

  navigatingBetweenProductVariants(prevUrl, nextUrl) {
    const prevUrlObject = this.getBaseProduct(prevUrl);
    const nextUrlObject = this.getBaseProduct(nextUrl);

    return prevUrlObject.firstPart === nextUrlObject.firstPart && prevUrlObject.lastPart === nextUrlObject.lastPart;
  }

  render() {
    const CategoryLinks = this.props.sitemap
      .get('children', Map())
      .filter(item => item.get('pageType') === 'CategoryLink');

    // Dynamically create routes to all category link pages
    const categoryLinkRoutes = CategoryLinks.map(item => (
      <Route
        exact
        key={item.get('id')}
        path={item.get('url')}
        render={props => <CategoryLink item={item} {...props} />}
      />
    )).toArray();

    const mainCategoryRoutes = CategoryLinks.map(item => (
      <Route
        exact
        key={item.get('id')}
        path={`${item.get('url')}/:categoryId`}
        render={props => {
          if (item.getIn(['children', 0, 'children']).size === 0) {
            return (
              <CategoryPage
                item={item
                  .get('children', List())
                  .find(child => child.get('url') === `${item.get('url')}/${props.match.params.categoryId}`)}
                {...props}
              />
            );
          }
          return (
            <ParentCategoryPage
              item={item
                .get('children', List())
                .find(child => child.get('url') === `${item.get('url')}/${props.match.params.categoryId}`)}
              {...props}
            />
          );
        }}
      />
    )).toArray();

    const subCategoryRoutes = CategoryLinks.map(item => (
      <Route
        exact
        key={item.get('id')}
        path={`${item.get('url')}/:categoryId/:subCategoryId`}
        render={props => (
          <CategoryPage
            item={item
              .get('children', List())
              .find(child => child.get('url') === `${item.get('url')}/${props.match.params.categoryId}`)
              .get('children', List())
              .find(
                child =>
                  child.get('url') ===
                  `${item.get('url')}/${props.match.params.categoryId}/${props.match.params.subCategoryId}`
              )}
            {...props}
          />
        )}
      />
    )).toArray();

    const productRoutes = CategoryLinks.map(item => (
      <Route
        exact
        key={item.get('id')}
        path={`${item.get('url')}/:categoryId/:subCategoryId/:productId`}
        render={props => <ProductPage {...props} />}
      />
    )).toArray();

    const shallowProductRoutes = CategoryLinks.filter(
      mainCategory => mainCategory.get('children').reduce((memory, item) => memory + item.get('children').size, 0) === 0
    )
      .map(item => (
        <Route
          exact
          key={item.get('id')}
          path={`${item.get('url')}/:subCategoryId/:productId`}
          render={props => <ProductPage {...props} />}
        />
      ))
      .toArray();

    // Checkout Page Route
    const checkoutRoute = this.props.sitemap
      .get('children', Map())
      .filter(item => item.get('pageType') === CHECKOUT_PAGE_TYPE)
      .map(item => (
        <Route key={item.get('id')} exact path={item.get('url')} render={props => <CheckoutPage {...props} />} />
      ))
      .toArray();

    // Campaign List Page Route
    const campaignListRoute = this.props.sitemap
      .get('children', Map())
      .filter(item => item.get('pageType') === CAMPAIGN_LIST_PAGE_TYPE)
      .map(item => (
        <Route key={item.get('id')} exact path={item.get('url')} render={props => <CampaignListPage {...props} />} />
      ))
      .toArray();

    // Campaign Page in root Route
    const campaignRootRoute = this.props.sitemap
      .get('children', Map())
      .filter(item => item.get('pageType') === CAMPAIGN_PAGE_TYPE)
      .map(item => (
        <Route key={item.get('id')} exact path={item.get('url')} render={props => <CampaignPage {...props} />} />
      ))
      .toArray();

    // Campaign Page Route
    const campaignRoute = this.props.sitemap
      .get('children', Map())
      .filter(item => item.get('pageType') === CAMPAIGN_LIST_PAGE_TYPE)
      .map(item => (
        <Route
          key={item.get('id')}
          exact
          path={`${item.get('url')}/:brandPage`}
          render={props => <CampaignPage {...props} />}
        />
      ))
      .toArray();

    // Campaign Page Route
    const productListRoute = this.props.sitemap
      .get('children', Map())
      .filter(item => item.get('pageType') === PRODUCT_LIST_PAGE_TYPE)
      .map(item => (
        <Route key={item.get('id')} exact path={item.get('url')} render={props => <ProductListPage {...props} />} />
      ))
      .toArray();

    // Newsletter Page Route
    const newsletterRoute = this.props.sitemap
      .get('children', Map())
      .filter(item => item.get('pageType') === NEWSLETTER_PAGE_TYPE)
      .map(item => (
        <Route key={item.get('id')} exact path={item.get('url')} render={props => <NewsletterPage {...props} />} />
      ))
      .toArray();

    return (
      <div>
        <Header />
        <CartWrapper />
        <Switch>
          <Route exact path="/" render={props => <StartPage {...props} />} />
          <Route exact path="/404" render={props => <ErrorPage title="404" {...props} />} />
          <Route exact path="/error" render={props => <ErrorPage title="Error" {...props} />} />
          <Route exact path="/varumarken" render={props => <BrandListPage type="brands" {...props} />} />
          <Route exact path="/varumarken/:brandPage" render={props => <BrandPage type="brand" {...props} />} />
          <Route exact path="/formgivare" render={props => <BrandListPage type="designers" {...props} />} />
          <Route exact path="/formgivare/:id" render={props => <BrandPage type="designer" {...props} />} />
          <Route path="/sok/:searchQuery" render={props => <SearchResultPage {...props} />} />
          {shallowProductRoutes}
          {categoryLinkRoutes}
          {mainCategoryRoutes}
          {subCategoryRoutes}
          {productRoutes}
          {checkoutRoute}
          {campaignListRoute}
          {campaignRootRoute}
          {campaignRoute}
          {productListRoute}
          {newsletterRoute}
          <Route exact path="/butiksexemplar" render={props => <StoreItemListPage {...props} />} />
          <Route exact path="/butiksexemplar/:productUrl" render={props => <StoreItemPage {...props} />} />
          <Route exact path="/tack" render={props => <OrderConfirmationPage title="Tack" {...props} />} />
          <Route path="/:articleUrl" render={props => <ArticlePage {...props} />} />
          <Redirect to="/404" />
        </Switch>
        <Footer />
      </div>
    );
  }
}

Routes.defaultProps = {
  sitemap: Map()
};

Routes.propTypes = {
  removeViewstate: PropTypes.func.isRequired,
  removeGlobalSearch: PropTypes.func.isRequired,
  fetchCart: PropTypes.func.isRequired,
  sitemap: PropTypes.instanceOf(Map),
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired
};

export default connect(
  Routes,
  { removeViewstate, removeGlobalSearch, fetchCart },
  () => ({
    sitemap: fromJS(window.sitemap)
  })
);



// WEBPACK FOOTER //
// ./src/router/Routes.js